import { clamp } from '@/util/math'
import { MaybeComputedElementRef, useElementBounding } from '@vueuse/core'
import type confetti from 'canvas-confetti'
import DOMPurify from 'dompurify'
import { match } from 'ts-pattern'
import {
  ComponentPublicInstance,
  computed,
  CSSProperties,
  MaybeRefOrGetter,
  nextTick,
  readonly,
  Ref,
  ref,
  toRef,
  toValue,
  watch,
} from 'vue'

export const useId = (() => {
  let _id = 0
  return function useId(prefix = 'component') {
    return readonly(ref(`${prefix}-${_id++}`))
  }
})()

export function sanitizeHTML(
  html: string,
  preset?: 'inline-noninteractive' | 'safe-html' | 'strip',
  options: { allowedAttrs?: string[] | undefined } | undefined = undefined,
): string {
  const inlineElements = [
    'b',
    'big',
    'i',
    'small',
    'tt',
    'abbr',
    'acronym',
    'cite',
    'code',
    'dfn',
    'em',
    'kbd',
    'strong',
    'samp',
    'var',
    'br',
    'q',
    's',
    'span',
    'sub',
    'sup',
    'u',
  ]

  if (preset === 'inline-noninteractive') {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: inlineElements,
      ALLOWED_ATTR: options?.allowedAttrs ?? ['style', 'title'],
    })
  }

  if (preset === 'safe-html') {
    return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } })
  }

  return DOMPurify.sanitize(html, { ALLOWED_TAGS: [] })
}

function useElement(element: MaybeComputedElementRef) {
  return computed(() => unwrapElement(toValue(element)))
}

function unwrapElement(
  target: HTMLElement | SVGElement | ComponentPublicInstance | null | undefined,
) {
  if (target != null && typeof target === 'object' && '$el' in target) {
    const element = target.$el
    return element instanceof HTMLElement && element.nodeType == Node.ELEMENT_NODE ? element : null
  }
  return target
}

type Placement = ['bottom' | 'top' | 'top-align', 'left' | 'left-align' | 'right' | 'right-align']

export type UsePlacementOptions = {
  placement?: MaybeRefOrGetter<Placement> | undefined
  top?: {
    min?: MaybeRefOrGetter<number>
    max?: MaybeRefOrGetter<number>
    offset?: MaybeRefOrGetter<number>
  }
  left?: {
    min?: MaybeRefOrGetter<number>
    max?: MaybeRefOrGetter<number>
    offset?: MaybeRefOrGetter<number>
  }
}

export function usePlacement(
  anchor: MaybeComputedElementRef,
  attachment: MaybeComputedElementRef,
  options: UsePlacementOptions | undefined = undefined,
) {
  const placement = toRef(options?.placement ?? ['bottom', 'right']) as Ref<Placement>
  const _anchor = useElement(anchor)
  const _attachment = useElement(attachment)
  const {
    top: anchorTop,
    width: anchorWidth,
    height: anchorHeight,
    left: anchorLeft,
    update: updateAnchor,
  } = useElementBounding(_anchor)
  const {
    height: attachmentHeight,
    width: attachmentWidth,
    update: updateAttachment,
  } = useElementBounding(_attachment)
  const style = ref<CSSProperties | null>(null)
  const isClampedTop = ref(false)
  const isClampedLeft = ref(false)

  function update() {
    updateAnchor()
    updateAttachment()

    const [yAxis, xAxis] = toValue(placement)
    const _top = anchorTop.value + window.scrollY
    const top = match(yAxis)
      .with('bottom', () => _top + anchorHeight.value)
      .with('top', () => _top - attachmentHeight.value)
      .with('top-align', () => _top)
      .exhaustive()
    const _left = anchorLeft.value + window.scrollX
    const left = match(xAxis)
      .with('left', () => _left)
      .with('left-align', () => _left - attachmentWidth.value)
      .with('right', () => _left + anchorWidth.value - attachmentWidth.value)
      .with('right-align', () => _left + anchorWidth.value)
      .exhaustive()

    const topMin = toValue(options?.top?.min) ?? null
    const topMax = toValue(options?.top?.max) ?? null
    const offsetTop = top + (toValue(options?.top?.offset) ?? 0)
    const clampedTop = clamp(
      offsetTop,
      topMin,
      topMax !== null ? topMax - attachmentHeight.value : null,
    )
    isClampedTop.value = offsetTop !== clampedTop
    const leftMin = toValue(options?.left?.min) ?? null
    const leftMax = toValue(options?.left?.max) ?? null
    const offsetLeft = left + (toValue(options?.left?.offset) ?? 0)
    const clampedLeft = clamp(
      offsetLeft,
      leftMin,
      leftMax !== null ? leftMax - attachmentWidth.value : null,
    )
    isClampedLeft.value = offsetLeft !== clampedLeft

    style.value = {
      position: 'absolute',
      margin: '0',
      top: `${clampedTop}px`,
      left: `${clampedLeft}px`,
    }
  }

  const attrs = computed(() => {
    // TODO: transparently implement anchor positioning for browsers that support it
    return { style: style.value }
  })

  watch(
    [anchorTop, anchorLeft, anchorWidth, anchorHeight, attachmentWidth, attachmentHeight],
    update,
  )

  return { attrs, update, isClampedTop, isClampedLeft }
}

export function isElementVisibleInViewport(el: HTMLElement, partiallyVisible = false) {
  const { top, left, bottom, right } = el.getBoundingClientRect()
  const { innerHeight, innerWidth } = window
  return partiallyVisible
    ? ((top > 0 && top < innerHeight) || (bottom > 0 && bottom < innerHeight)) &&
        ((left > 0 && left < innerWidth) || (right > 0 && right < innerWidth))
    : top >= 0 && left >= 0 && bottom <= innerHeight && right <= innerWidth
}

export function useConfetti(targetEl: MaybeRefOrGetter<HTMLElement | null | undefined>) {
  const { top, width, left, update } = useElementBounding(targetEl)
  const x = computed(() => left.value + width.value / 2)
  const y = computed(() => top.value)
  const origin = computed(() => ({
    x: x.value / window.innerWidth,
    y: y.value / window.innerHeight,
  }))
  const _confetti = import('canvas-confetti')
  return async function fire(options: confetti.Options) {
    const confetti = (await _confetti).default
    await nextTick()
    update()
    await nextTick()
    return await confetti({
      origin: origin.value,
      disableForReducedMotion: true,
      ...options,
    })
  }
}

export async function retainScrollPosition(
  el: Element | undefined | null,
  callback: () => void | Promise<void>,
) {
  if (!el) return callback()
  const scrollTop = el.scrollTop
  await callback()
  await nextTick()
  await new Promise((resolve) => requestAnimationFrame(resolve))
  el.scrollTo({ top: scrollTop, behavior: 'instant' })
}
